import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseComponent } from '../../../base/base.component';
import { RadioButtonFieldComponent } from '../../../core/fields/radio-button-field.component';
import { SelectFieldComponent } from '../../../core/fields/select-field.component';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { ReportsService } from '../../../../services/reports/reports.service';
import { LossCodeItem } from '../../../../models/api/reports/loss-code-item';

@Component({
  selector: 'app-loss-code-criteria',
  imports: [CommonModule, RadioButtonFieldComponent, SelectFieldComponent],
  template: `
    <div class="tw-pt-2">
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        <app-radio-button-field
          [control]="fg.controls.icdVersion"
          optionLabel="label"
          optionValue="value"
          [options]="icdVersionOptions"
          [showHelpText]="false"
          [vertical]="false"
          label="ICD version"
          labelCase="none"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
        />

        <app-select-field
          [control]="fg.controls.icdCodes"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="icdCodeOptions"
          [showHelpText]="false"
          [virtualScroll]="true"
          [showToggleAll]="false"
          label="ICD code"
          labelCase="none"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [loading]="loadingIcd"
        />

        <app-select-field
          [control]="fg.controls.sourceCodes"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="sourceCodeOptions"
          [showHelpText]="false"
          [virtualScroll]="true"
          [showToggleAll]="false"
          label="Source code"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [loading]="loadingSource"
        />

        <app-select-field
          [control]="fg.controls.causeCodes"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="causeCodeOptions"
          [showHelpText]="false"
          [virtualScroll]="true"
          [showToggleAll]="false"
          label="Cause code"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [loading]="loadingCause"
        />

        <app-select-field
          [control]="fg.controls.natureCodes"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="natureCodeOptions"
          [showHelpText]="false"
          [virtualScroll]="true"
          [showToggleAll]="false"
          label="Nature/Result code"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [loading]="loadingNature"
        />

        <app-select-field
          [control]="fg.controls.partCodes"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="partCodeOptions"
          [showHelpText]="false"
          [virtualScroll]="true"
          [showToggleAll]="false"
          label="Part/Target code"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [loading]="loadingPart"
        />
      </div>
    </div>
  `,
  styles: `
    :host ::ng-deep app-select-field,
    :host ::ng-deep app-radio-button-field {
      display: block;
      min-width: 0;
    }
    :host ::ng-deep .p-select-label > .option-label,
    :host ::ng-deep .p-multiselect-label .option-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
})
export class LossCodeCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  @Input({ required: true })
  section: FilterSection;

  private reports = inject(ReportsService);
  private contractNumber: number;

  loadingIcd = false;
  loadingSource = false;
  loadingCause = false;
  loadingNature = false;
  loadingPart = false;

  private allIcdCodes: LossCodeItem[] = [];
  icdCodeOptions: LossCodeItem[] = [];
  sourceCodeOptions: LossCodeItem[] = [];
  causeCodeOptions: LossCodeItem[] = [];
  natureCodeOptions: LossCodeItem[] = [];
  partCodeOptions: LossCodeItem[] = [];

  icdVersionOptions = [
    { label: 'ICD 9', value: '9' },
    { label: 'ICD 10', value: '10' },
    { label: 'Both', value: '' },
  ];

  fg = new FormGroup({
    icdVersion: new FormControl<string>(''),
    icdCodes: new FormControl<string[]>([]),
    sourceCodes: new FormControl<string[]>([]),
    causeCodes: new FormControl<string[]>([]),
    natureCodes: new FormControl<string[]>([]),
    partCodes: new FormControl<string[]>([]),
  });

  constructor() {
    super();
  }

  setLossCodesFromCriteria(criteria: FilterCriteria[]) {
    const icdCodes = criteria
      .filter((c) => c.column === 'VERSIONED_ICD_CODE_LIST')
      .flatMap((c) => c.values);

    const sourceCodes = criteria
      .filter((c) => c.column === 'SOURCE_CODE')
      .flatMap((c) => c.values);

    const causeCodes = criteria
      .filter((c) => c.column === 'CAUSE_CODE')
      .flatMap((c) => c.values);

    const natureCodes = criteria
      .filter((c) => c.column === 'NATURE_RESULT_CODE')
      .flatMap((c) => c.values);

    const partCodes = criteria
      .filter((c) => c.column === 'PART_TARGET_CODE')
      .flatMap((c) => c.values);

    if (icdCodes.length) this.fg.controls.icdCodes.setValue(icdCodes);
    if (sourceCodes.length) this.fg.controls.sourceCodes.setValue(sourceCodes);
    if (causeCodes.length) this.fg.controls.causeCodes.setValue(causeCodes);
    if (natureCodes.length) this.fg.controls.natureCodes.setValue(natureCodes);
    if (partCodes.length) this.fg.controls.partCodes.setValue(partCodes);
  }

  ngOnInit() {
    this.section.criteria = this;
    this.dispose.subs = this.fg.valueChanges.subscribe(() => this.emitCount());

    this.dispose.subs = this.fg.controls.icdVersion.valueChanges.subscribe(() =>
      this.filterIcdCodes(),
    );

    const client = this.reports.selectedClient();
    if (client) {
      this.contractNumber = Number(client);
      this.loadAllCodes();
    }
  }

  private async loadAllCodes() {
    await Promise.all([
      this.loadIcdCodes(),
      this.loadSourceCodes(),
      this.loadCauseCodes(),
      this.loadNatureCodes(),
      this.loadPartCodes(),
    ]);

    if (this.section.values?.length) {
      this.setLossCodesFromCriteria(this.section.values);
    }
  }

  private async loadIcdCodes() {
    this.loadingIcd = true;
    this.allIcdCodes = await this.api.filtersCriteria.getIcdCodes();
    this.filterIcdCodes();
    this.loadingIcd = false;
  }

  private filterIcdCodes(): void {
    const version = this.fg.controls.icdVersion.value;
    const selectedValues = this.fg.controls.icdCodes.value || [];

    const filtered = version
      ? this.allIcdCodes.filter((c) => c.version === version)
      : this.allIcdCodes;

    const selectedCodes = this.allIcdCodes.filter((c) =>
      selectedValues.includes(c.value),
    );

    this.icdCodeOptions = [
      ...selectedCodes,
      ...filtered.filter((c) => !selectedValues.includes(c.value)),
    ];
  }

  private async loadSourceCodes() {
    this.loadingSource = true;
    this.sourceCodeOptions = await this.api.filtersCriteria.getSourceCodes(
      this.contractNumber,
    );
    this.loadingSource = false;
  }

  private async loadCauseCodes() {
    this.loadingCause = true;
    this.causeCodeOptions = await this.api.filtersCriteria.getCauseCodes(
      this.contractNumber,
      'CAUSE2',
    );
    this.loadingCause = false;
  }

  private async loadNatureCodes() {
    this.loadingNature = true;
    this.natureCodeOptions = await this.api.filtersCriteria.getCauseCodes(
      this.contractNumber,
      'CAUSE3',
    );
    this.loadingNature = false;
  }

  private async loadPartCodes() {
    this.loadingPart = true;
    this.partCodeOptions = await this.api.filtersCriteria.getCauseCodes(
      this.contractNumber,
      'CAUSE4',
    );
    this.loadingPart = false;
  }

  private emitCount(): void {
    const count =
      (this.fg.controls.icdCodes.value?.length || 0) +
      (this.fg.controls.sourceCodes.value?.length || 0) +
      (this.fg.controls.causeCodes.value?.length || 0) +
      (this.fg.controls.natureCodes.value?.length || 0) +
      (this.fg.controls.partCodes.value?.length || 0);

    this.section.count.set(count);
  }

  clearCriteria(): void {
    this.fg.controls.icdCodes.setValue([], { emitEvent: false });
    this.fg.controls.sourceCodes.setValue([], { emitEvent: false });
    this.fg.controls.causeCodes.setValue([], { emitEvent: false });
    this.fg.controls.natureCodes.setValue([], { emitEvent: false });
    this.fg.controls.partCodes.setValue([], { emitEvent: false });
    this.section.count.set(0);
  }

  valid(): boolean {
    return this.fg.valid;
  }

  getCriteria(): FilterCriteria[] {
    const icdCriteria = (this.fg.controls.icdCodes.value || []).map((value) => {
      return {
        tableAlias: 'claim_icd_codes',
        column: 'VERSIONED_ICD_CODE_LIST',
        displayName: '',
        operator: 'LIKE',
        values: [value],
        lineOfBusiness: '',
        dataType: 'VARCHAR2',
        isClaimLab: false,
        formattedOperator: '',
        formattedValue: '',
        filterSection: 'Loss',
        filterSubsection: '',
      } as FilterCriteria;
    });

    const sourceCriteria = (this.fg.controls.sourceCodes.value || []).map(
      (value) =>
        ({
          tableAlias: 'claim_loss_info',
          column: 'SOURCE_CODE',
          displayName: '',
          operator: 'LIKE',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Loss',
          filterSubsection: '',
        } as FilterCriteria),
    );

    const causeCriteria = (this.fg.controls.causeCodes.value || []).map(
      (value) =>
        ({
          tableAlias: 'claim',
          column: 'CAUSE_CODE',
          displayName: '',
          operator: 'LIKE',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Loss',
          filterSubsection: '',
        } as FilterCriteria),
    );

    const natureCriteria = (this.fg.controls.natureCodes.value || []).map(
      (value) =>
        ({
          tableAlias: 'claim',
          column: 'NATURE_RESULT_CODE',
          displayName: '',
          operator: 'LIKE',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Loss',
          filterSubsection: '',
        } as FilterCriteria),
    );

    const partCriteria = (this.fg.controls.partCodes.value || []).map(
      (value) =>
        ({
          tableAlias: 'claim',
          column: 'PART_TARGET_CODE',
          displayName: '',
          operator: 'LIKE',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Loss',
          filterSubsection: '',
        } as FilterCriteria),
    );

    return [
      ...icdCriteria,
      ...sourceCriteria,
      ...causeCriteria,
      ...natureCriteria,
      ...partCriteria,
    ];
  }
}
